/**
 * RegistryTable.js
 * Shows all registered cards with options to edit or delete.
 */

import React, { useState } from "react";

export default function RegistryTable({ registry, onEdit, onDelete }) {
  const [search, setSearch] = useState("");

  const entries = Object.entries(registry)
    .filter(([uid, info]) => {
      const q = search.toLowerCase();
      return (
        uid.toLowerCase().includes(q) ||
        info.name.toLowerCase().includes(q) ||
        info.studentId.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a[1].name.localeCompare(b[1].name));

  return (
    <div className="glass-card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center
                      justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-800">Registered Cards</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {Object.keys(registry).length} card{Object.keys(registry).length !== 1 ? "s" : ""} registered
          </p>
        </div>

        {/* Search */}
        <div className="relative min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
               viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or ID..."
            className="w-full pl-8 pr-3 py-1.5 rounded-xl text-xs border border-slate-200
                       bg-white focus:outline-none focus:ring-2 focus:ring-brand-300
                       placeholder-slate-400 text-slate-700"
          />
        </div>
      </div>

      {/* Table */}
      {entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3 text-center">
          <div className="text-3xl">🪪</div>
          <p className="font-semibold text-slate-500 text-sm">No cards registered yet</p>
          <p className="text-xs text-slate-400">
            Click "Register Card" or use the register button on a scanned UID.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                {["#", "Name", "Student / Emp. ID", "Card UID", "Registered At", ""].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold
                                          text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map(([uid, info], idx) => (
                <tr key={uid} className="hover:bg-brand-50/30 transition-colors row-enter">
                  <td className="px-5 py-3 text-xs text-slate-400 font-medium">
                    {idx + 1}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {/* Avatar */}
                      <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center
                                      justify-center text-brand-700 text-xs font-bold flex-shrink-0">
                        {info.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-800 text-sm">
                        {info.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 text-xs font-mono font-medium">
                    {info.studentId}
                  </td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-xs font-semibold text-slate-700
                                     bg-slate-100 px-2.5 py-0.5 rounded-lg tracking-wider">
                      {uid}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-400 whitespace-nowrap">
                    {info.registeredAt}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => onEdit(uid, info)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600
                                   hover:bg-brand-50 transition-colors"
                        title="Edit"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => onDelete(uid)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600
                                   hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14H6L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
